import React, { Suspense, lazy } from 'react';
import {Route, Routes, useLocation} from 'react-router-dom';
import Home from './pages/Home';
import DealLanding from './pages/DealLanding';
import DealSearch from './pages/DealSearch';
import BestInCategory from './pages/BestInCategory';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import NewsletterPopup from './components/NewsletterPopup';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import ScrollControls from './components/ScrollControls';
import PageViewTracker from './components/PageViewTracker';
import GoogleAnalytics from './components/GoogleAnalytics';
import MetaPixel from './components/MetaPixel';
import {AccountProvider} from './context/AccountContext';
import {FavoritesProvider} from './context/FavoritesContext';
import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ADMIN_PATH, ADMIN_CHANGE_PASSWORD_PATH, ADMIN_RESET_PASSWORD_PATH } from './config/adminRoutes';

const ComparisonLanding = lazy(() => import('./pages/ComparisonLanding'));

// Las rutas secundarias se descargan solo cuando se visitan. Checkout sigue
// siendo el unico sitio que necesita Stripe/PayPal; la home tampoco necesita
// el codigo de legales, comparador, detalle, cupones o paneles privados.
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminChangePassword = lazy(() => import('./pages/admin/AdminChangePassword'));
const AdminResetPassword = lazy(() => import('./pages/admin/AdminResetPassword'));
const SellerDashboard = lazy(() => import('./pages/seller/SellerDashboard'));
const CartCard = lazy(() => import('./pages/CartCard'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const DealDetail = lazy(() => import('./pages/DealDetail'));
const ProductComparePage = lazy(() => import('./pages/ProductComparePage'));
const Stores = lazy(() => import('./pages/Stores'));
const Coupons = lazy(() => import('./pages/Coupons'));
const Contact = lazy(() => import('./pages/Contact'));
const AvisoLegal = lazy(() => import('./pages/AvisoLegal'));
const QuienesSomos = lazy(() => import('./pages/QuienesSomos'));
const PriceTrendReport = lazy(() => import('./pages/PriceTrendReport'));
const NeedsFinder = lazy(() => import('./pages/NeedsFinder'));
const AmazonStore = lazy(() => import('./pages/AmazonStore'));
const PoliticaPrivacidad = lazy(() => import('./pages/PoliticaPrivacidad'));
const PoliticaCookies = lazy(() => import('./pages/PoliticaCookies'));
const MyAlerts = lazy(() => import('./pages/MyAlerts'));
const MyFavorites = lazy(() => import('./pages/MyFavorites'));
const Games = lazy(() => import('./pages/Games'));
const PriceDuelGame = lazy(() => import('./pages/games/PriceDuelGame'));
const ProductMemoryGame = lazy(() => import('./pages/games/ProductMemoryGame'));
const RouletteGame = lazy(() => import('./pages/games/RouletteGame'));
const DiscountGuessGame = lazy(() => import('./pages/games/DiscountGuessGame'));
const ReflexGame = lazy(() => import('./pages/games/ReflexGame'));
const StoreGuessGame = lazy(() => import('./pages/games/StoreGuessGame'));
const PriceSortGame = lazy(() => import('./pages/games/PriceSortGame'));
const NotFound = lazy(() => import('./pages/NotFound'));

function App() {
    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith(ADMIN_PATH);

    return (
        <AccountProvider>
        <FavoritesProvider>
            <div>
                <ScrollToTop />
                <PageViewTracker />
                <GoogleAnalytics />
                {!isAdminRoute && <a className="skip-link" href="#main-content">Saltar al contenido principal</a>}
                {!isAdminRoute && <Header />}
                <div id="main-content" tabIndex="-1">
                    <ErrorBoundary key={location.pathname}>
                        <Suspense fallback={<div className="route-loading" role="status">Cargando página...</div>}>
                            <Routes>
                                <Route path={ADMIN_PATH} element={<AdminDashboard />} />
                                <Route path={ADMIN_CHANGE_PASSWORD_PATH} element={<AdminChangePassword />} />
                                <Route path={ADMIN_RESET_PASSWORD_PATH} element={<AdminResetPassword />} />
                                <Route path="/" element={<Home />} />
                                <Route path="/buscar" element={<DealSearch />} />
                                <Route path="/comparar" element={<ProductComparePage />} />
                                <Route path="/ofertas/:dealId" element={<DealDetail />} />
                                <Route path="/chollos/:categorySlug" element={<DealLanding type="category" />} />
                                <Route path="/chollos/:categorySlug/pagina/:page" element={<DealLanding type="category" />} />
                                <Route path="/mejores/:categorySlug" element={<BestInCategory />} />
                                <Route path="/comparativas/:slug" element={<ComparisonLanding />} />
                                <Route path="/tiendas" element={<Stores />} />
                                <Route path="/cupones" element={<Coupons />} />
                                <Route path="/cupones/:storeSlug" element={<Coupons />} />
                                <Route path="/juegos" element={<Games />} />
                                <Route path="/juegos/cual-es-mas-caro" element={<PriceDuelGame />} />
                                <Route path="/juegos/memoria-chollos" element={<ProductMemoryGame />} />
                                <Route path="/juegos/ruleta" element={<RouletteGame />} />
                                <Route path="/juegos/adivina-el-descuento" element={<DiscountGuessGame />} />
                                <Route path="/juegos/caza-el-chollo" element={<ReflexGame />} />
                                <Route path="/juegos/de-que-tienda-es" element={<StoreGuessGame />} />
                                <Route path="/juegos/ordena-por-precio" element={<PriceSortGame />} />
                                <Route path="/tiendas/:storeSlug" element={<DealLanding type="store" />} />
                                <Route path="/tiendas/:storeSlug/pagina/:page" element={<DealLanding type="store" />} />
                                <Route path="/mis-alertas" element={<MyAlerts />} />
                                <Route path="/mis-favoritos" element={<MyFavorites />} />
                                <Route path="/vendedor" element={<SellerDashboard />} />
                                <Route path="/vendedor/:sellerId" element={<SellerDashboard />} />
                                <Route path="/cart" element={<CartCard />} />
                                <Route path="/checkout" element={<CheckoutPage />} />
                                <Route path="/contact" element={<Contact />} />
                                <Route path="/aviso-legal" element={<AvisoLegal />} />
                                <Route path="/quienes-somos" element={<QuienesSomos />} />
                                <Route path="/informe-precios" element={<PriceTrendReport />} />
                                <Route path="/buscador-inteligente" element={<NeedsFinder />} />
                                <Route path="/amazon" element={<AmazonStore />} />
                                <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
                                <Route path="/politica-cookies" element={<PoliticaCookies />} />
                                <Route path="*" element={<NotFound />} />
                            </Routes>
                        </Suspense>
                    </ErrorBoundary>
                </div>
                {!isAdminRoute && <Footer />}
                {!isAdminRoute && <ScrollControls />}
                {!isAdminRoute && <CookieConsent />}
                {!isAdminRoute && <NewsletterPopup />}
                {!isAdminRoute && <MetaPixel />}
                <ToastContainer position="top-right" autoClose={3000} />
            </div>
        </FavoritesProvider>
        </AccountProvider>
    );
}

export default App;
